import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { Search, X, Heart, Activity, AlertTriangle, Baby } from "lucide-react";
import { ResponsivePie } from "@nivo/pie";
import { Card } from "../../styles/GlobalStyles";

const atestadosData = require("../../data/atestados-data.json");

const AtestadosContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 0 16px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 12px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    margin-bottom: 8px;
  }
`;

const FilterContainer = styled.div`
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 16px;
    margin-bottom: 16px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
    border-radius: 6px;
  }
`;

const FilterGrid = styled.div`
  display: flex;
  align-items: end;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 12px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: unset;
    width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: unset;
    width: 100%;
    padding: 10px 12px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
    font-size: 16px; /* Evita zoom em iOS */
  }
`;

const SearchButton = styled.button`
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  height: 38px;

  &:hover {
    background-color: #2563eb;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
    height: auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 14px 16px;
    font-size: 16px;
  }
`;

const ClearButton = styled.button`
  background: none;
  color: #f97316;
  border: none;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px 0;
  height: 38px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #ea580c;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: center;
    padding: 12px 0;
    height: auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 14px 0;
    font-size: 16px;
  }
`;

const TopCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: 0;
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 ${({ theme }) => theme.spacing.lg} 0
    ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg} 0
    ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  text-align: left;
  width: auto;
  flex-shrink: 0;

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const AtestadosVigorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: 16px;
    padding: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 12px;
    padding: 12px;
  }
`;

const AtestadosChartSection = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 160px;
    height: 160px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 140px;
    height: 140px;
  }
`;

const AtestadosListSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AtestadoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AtestadoIcon = styled.div<{ color: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

const AtestadoText = styled.span`
  font-size: 14px;
  color: #374151;
  flex: 1;
`;

const AtestadoNumber = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  min-width: 20px;
  text-align: right;
`;

const ChartCenterContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
`;

const ChartCenterValue = styled.div`
  font-size: 40px;
  font-weight: bold;
  color: #1f2937;
  line-height: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 32px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 24px;
  }
`;

const SimpleStatsCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  flex-direction: column;
  min-height: 200px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    min-height: 160px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 16px;
    min-height: 140px;
  }
`;

const SimpleStatsTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  width: 100%;
  text-align: left;
  flex-shrink: 0;
`;

const SimpleStatsContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SimpleStatsValue = styled.div`
  font-size: 40px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 32px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 28px;
  }
`;

const SimpleStatsSubtitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const TableCard = styled(Card)`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: 0;
  overflow: hidden;
`;

const TableTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 ${({ theme }) => theme.spacing.lg} 0
    ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg} 0
    ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  text-align: left;
  width: auto;
  flex-shrink: 0;
`;

const TableContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: 500px; /* Força largura mínima para scroll horizontal */
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 400px;
  }
`;

const TableHeader = styled.th`
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 8px;
  }
`;

const TableRow = styled.tr`
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 8px;
  }
`;

export const Atestados: React.FC = () => {
  const [filters, setFilters] = useState({
    empresa: "",
    unidade: "",
    departamento: "",
    periodo: "",
  });

  const filteredData = useMemo(() => {
    let filtered = { ...atestadosData };

    // Filtragem por departamento
    if (filters.departamento && filters.departamento !== "") {
      filtered.departamentosData = atestadosData.departamentosData.filter(
        (dept: any) =>
          dept.departamento
            .toLowerCase()
            .includes(filters.departamento.toLowerCase())
      );

      // Também filtrar os atestados por departamento se necessário
      const totalFiltrado = filtered.departamentosData.reduce(
        (sum: number, dept: any) => sum + dept.total,
        0
      );

      // Recalcular proporções dos tipos de atestados baseado no filtro
      if (totalFiltrado > 0) {
        filtered.atestadosData = atestadosData.atestadosData.map(
          (item: any) => ({
            ...item,
            count: Math.round((item.count / 38) * totalFiltrado), // Proporção baseada no total filtrado
          })
        );
      }
    }

    // Filtragem por empresa
    if (filters.empresa && filters.empresa !== "") {
      // Aplicar filtro de empresa (simulação)
      const fatorEmpresa =
        filters.empresa === "empresa1"
          ? 0.6
          : filters.empresa === "empresa2"
          ? 0.3
          : 1;

      filtered.departamentosData = filtered.departamentosData.map(
        (dept: any) => ({
          ...dept,
          total: Math.round(dept.total * fatorEmpresa),
        })
      );

      filtered.atestadosData = filtered.atestadosData.map((item: any) => ({
        ...item,
        count: Math.round(item.count * fatorEmpresa),
      }));
    }

    // Filtragem por período
    if (filters.periodo && filters.periodo !== "") {
      const fatorPeriodo =
        filters.periodo === "30dias"
          ? 0.3
          : filters.periodo === "90dias"
          ? 0.7
          : 1;

      filtered.departamentosData = filtered.departamentosData.map(
        (dept: any) => ({
          ...dept,
          total: Math.round(dept.total * fatorPeriodo),
        })
      );

      filtered.atestadosData = filtered.atestadosData.map((item: any) => ({
        ...item,
        count: Math.round(item.count * fatorPeriodo),
      }));
    }

    return filtered;
  }, [filters]);

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      empresa: "",
      unidade: "",
      departamento: "",
      periodo: "",
    });
  };

  // Dados vêm do arquivo JSON
  const chartData = filteredData.atestadosData.map((item: any) => ({
    id: item.tipo,
    label: item.tipo,
    value: item.count,
    color: item.color,
  }));

  // Calcular totais dinâmicos baseados nos filtros
  const totalAtestados = filteredData.atestadosData.reduce(
    (sum: number, item: any) => sum + item.count,
    0
  );

  return (
    <AtestadosContainer>
      <PageHeader>
        <PageTitle>Atestados</PageTitle>
      </PageHeader>

      <FilterContainer>
        <FilterGrid>
          <FilterGroup>
            <FilterLabel>Empresa</FilterLabel>
            <FilterSelect
              value={filters.empresa}
              onChange={(e) => handleFilterChange("empresa", e.target.value)}
            >
              <option value="">Todas as empresas</option>
              <option value="empresa1">Empresa 1</option>
              <option value="empresa2">Empresa 2</option>
              <option value="empresa3">Empresa 3</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Unidade</FilterLabel>
            <FilterSelect
              value={filters.unidade}
              onChange={(e) => handleFilterChange("unidade", e.target.value)}
            >
              <option value="">Todas as unidades</option>
              <option value="matriz">Matriz</option>
              <option value="filial1">Filial 1</option>
              <option value="filial2">Filial 2</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Departamento</FilterLabel>
            <FilterSelect
              value={filters.departamento}
              onChange={(e) =>
                handleFilterChange("departamento", e.target.value)
              }
            >
              <option value="">Todos os departamentos</option>
              <option value="administrativo">Administrativo</option>
              <option value="operacoes">Operações</option>
              <option value="comercial">Comercial</option>
              <option value="servicos">Serviços e Qualidade</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Período</FilterLabel>
            <FilterSelect
              value={filters.periodo}
              onChange={(e) => handleFilterChange("periodo", e.target.value)}
            >
              <option value="">Todos os períodos</option>
              <option value="30dias">Últimos 30 dias</option>
              <option value="90dias">Últimos 90 dias</option>
              <option value="6meses">Últimos 6 meses</option>
              <option value="1ano">Último ano</option>
            </FilterSelect>
          </FilterGroup>

          <SearchButton>
            <Search size={16} />
            Buscar
          </SearchButton>

          <ClearButton onClick={clearFilters}>
            <X size={16} />
            Limpar filtros
          </ClearButton>
        </FilterGrid>
      </FilterContainer>

      <TopCardsGrid>
        <ChartCard>
          <ChartTitle>Atestados em vigor</ChartTitle>
          <AtestadosVigorContainer>
            <AtestadosChartSection>
              <ResponsivePie
                data={chartData}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                innerRadius={0.65}
                padAngle={2}
                cornerRadius={0}
                colors={{ datum: "data.color" }}
                borderWidth={0}
                enableArcLinkLabels={false}
                enableArcLabels={false}
                animate={true}
                motionConfig="gentle"
              />
              <ChartCenterContainer>
                <ChartCenterValue>{totalAtestados}</ChartCenterValue>
              </ChartCenterContainer>
            </AtestadosChartSection>

            <AtestadosListSection>
              {filteredData.atestadosData.map((item: any, index: number) => (
                <AtestadoItem key={item.id}>
                  <AtestadoIcon color={item.color}>
                    {index === 0 && <Heart size={14} />}
                    {index === 1 && <Activity size={14} />}
                    {index === 2 && <AlertTriangle size={14} />}
                    {index === 3 && <Baby size={14} />}
                  </AtestadoIcon>
                  <AtestadoNumber>{item.count}</AtestadoNumber>
                  <AtestadoText>{item.tipo}</AtestadoText>
                </AtestadoItem>
              ))}
            </AtestadosListSection>
          </AtestadosVigorContainer>
        </ChartCard>

        <SimpleStatsCard>
          <SimpleStatsTitle>Colaboradores afastados</SimpleStatsTitle>
          <SimpleStatsContent>
            <SimpleStatsValue>{totalAtestados}</SimpleStatsValue>
            <SimpleStatsSubtitle>
              <span style={{ color: "#10B981" }}>↓ 11%</span> em relação aos
              últimos 30 dias
            </SimpleStatsSubtitle>
          </SimpleStatsContent>
        </SimpleStatsCard>

        <SimpleStatsCard>
          <SimpleStatsTitle>Tempo médio por atestado</SimpleStatsTitle>
          <SimpleStatsContent>
            <SimpleStatsValue>5 dias</SimpleStatsValue>
            <SimpleStatsSubtitle>
              <span style={{ color: "#F59E0B" }}>↑ 3%</span> em relação aos
              últimos 30 dias
            </SimpleStatsSubtitle>
          </SimpleStatsContent>
        </SimpleStatsCard>
      </TopCardsGrid>

      <ContentGrid>
        <TableCard>
          <TableTitle>Principais motivos dos afastamentos (CID's)</TableTitle>
          <TableContent>
            <Table>
              <thead>
                <tr>
                  <TableHeader>CID</TableHeader>
                  <TableHeader>Descrição</TableHeader>
                  <TableHeader>Nº total de atestados</TableHeader>
                </tr>
              </thead>
              <tbody>
                {filteredData.motivosData.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{item.cid}</TableCell>
                    <TableCell>{item.descricao}</TableCell>
                    <TableCell>{item.total}</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContent>
        </TableCard>

        <TableCard>
          <TableTitle>Distribuição por setor/departamento</TableTitle>
          <TableContent>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Departamento</TableHeader>
                  <TableHeader>Nº total de atestados</TableHeader>
                </tr>
              </thead>
              <tbody>
                {filteredData.departamentosData.map(
                  (item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{item.departamento}</TableCell>
                      <TableCell>{item.total}</TableCell>
                    </TableRow>
                  )
                )}
              </tbody>
            </Table>
          </TableContent>
        </TableCard>
      </ContentGrid>
    </AtestadosContainer>
  );
};
